import { parsePhoneNumber, isValidPhoneNumber, AsYouType, type CountryCode } from 'libphonenumber-js';

export interface PhoneValidationResult {
  isValid: boolean;
  error?: string;
  formattedNumber?: string;
  nationalNumber?: string;
  internationalNumber?: string;
  countryCode?: string;
}

/**
 * Comprehensive phone number validation and formatting
 * @param phoneNumber - The phone number to validate
 * @param countryCode - The country code (e.g., 'US', 'CA', 'GB')
 * @returns Validation result with formatted numbers
 */
export const validateAndFormatPhone = (phoneNumber: string, countryCode: string): PhoneValidationResult => {
  if (!phoneNumber || phoneNumber.trim().length === 0) {
    return { isValid: false, error: 'Phone number is required' };
  }

  try {
    // Parse the phone number with country context
    const parsed = parsePhoneNumber(phoneNumber, countryCode as CountryCode);
    
    if (!parsed) {
      return { isValid: false, error: 'Invalid phone number format' };
    }

    // Check if the number is valid
    if (!parsed.isValid()) {
      return { isValid: false, error: 'Please enter a valid phone number for ' + countryCode };
    }

    // Return all formatted versions
    return { 
      isValid: true, 
      formattedNumber: parsed.formatInternational(), // +1 234 567 8900
      nationalNumber: parsed.formatNational(), // (234) 567-8900
      countryCode: parsed.country
    };
  } catch (error) {
    return { isValid: false, error: 'Invalid phone number format' };
  }
};

/**
 * Format phone number as user types (for real-time formatting)
 * @param phoneNumber - The phone number being typed
 * @param countryCode - The country code
 * @returns Formatted phone number
 */
export const formatPhoneAsYouType = (phoneNumber: string, countryCode: string): string => {
  try {
    const formatter = new AsYouType(countryCode as CountryCode);
    return formatter.input(phoneNumber);
  } catch (error) {
    return phoneNumber; // Return original if formatting fails
  }
};

/**
 * Extract country code from phone number
 * @param phoneNumber - International phone number
 * @returns Country code or null
 */
export const getCountryFromPhone = (phoneNumber: string): string | null => {
  try {
    const parsed = parsePhoneNumber(phoneNumber);
    return parsed?.country || null;
  } catch (error) {
    return null;
  }
};

/**
 * Check if a phone number is valid without country context
 * @param phoneNumber - The phone number to check
 * @returns Boolean indicating validity
 */
export const isPhoneValid = (phoneNumber: string): boolean => {
  try {
    return isValidPhoneNumber(phoneNumber);
  } catch (error) {
    return false;
  }
};

/**
 * Convert phone number to E.164 format (for database storage)
 * @param phoneNumber - The phone number
 * @param countryCode - The country code
 * @returns E.164 formatted number or null
 */
export const toE164Format = (phoneNumber: string, countryCode: string): string | null => {
  try {
    const parsed = parsePhoneNumber(phoneNumber, countryCode as CountryCode);
    return parsed?.format('E.164') || null;
  } catch (error) {
    return null;
  }
};

/**
 * Get phone number type (mobile, landline, etc.)
 * @param phoneNumber - The phone number
 * @param countryCode - The country code
 * @returns Phone number type
 */
export const getPhoneType = (phoneNumber: string, countryCode: string): string | null => {
  try {
    const parsed = parsePhoneNumber(phoneNumber, countryCode as CountryCode);
    return parsed?.getType() || null;
  } catch (error) {
    return null;
  }
};

/**
 * Format phone number for display (user-friendly format)
 * Handles various input types and formats the number with proper country code detection
 * @param phoneNumber - The phone number to format (can be string, number, undefined, or null)
 * @returns Formatted phone number string or empty string if input is invalid
 */
export const formatPhoneForDisplay = (phoneNumber: string | number | undefined | null): string => {
  if (!phoneNumber) {
    return '';
  }
  
  try {
    // Convert to string first (handles both string and number inputs)
    const raw = String(phoneNumber);
    const normalized = raw.startsWith('+') ? raw : `+${raw}`;
    const iso = getCountryFromPhone(normalized);
    const formatted = iso ? validateAndFormatPhone(normalized, iso).formattedNumber : normalized;
    return formatted || normalized;
  } catch (error) {
    return String(phoneNumber);
  }
};
